import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, Loader2, CreditCard, User, Building2 } from "lucide-react";
import { startCheckout } from "../lib/payments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCIES = {
  ponctuel: { id: "ponctuel", label: "Ponctuel", factor: 1 },
  hebdo: { id: "hebdo", label: "Hebdomadaire", factor: 3.6 },
  quotidien: { id: "quotidien", label: "Quotidien", factor: 14 },
};

const PROFILES = {
  particulier: {
    id: "particulier",
    label: "Particulier",
    icon: User,
    spaces: [
      { id: "copropriete", label: "Logement", rate: 0.7 },
      { id: "vitres", label: "Vitres", rate: 1.3 },
      { id: "chantier", label: "Remise en état", rate: 2.4 },
      { id: "automobile", label: "Véhicule (intérieur)", rate: 0 },
    ],
    frequencies: ["ponctuel", "hebdo"],
    placeholder: "Précisions utiles (étage, accès, animaux, véhicule...)",
  },
  professionnel: {
    id: "professionnel",
    label: "Professionnel",
    icon: Building2,
    spaces: [
      { id: "bureaux", label: "Bureaux / locaux", rate: 0.9 },
      { id: "vitres", label: "Vitres & façades", rate: 1.3 },
      { id: "chantier", label: "Fin de chantier", rate: 2.4 },
      { id: "automobile", label: "Véhicules / flotte", rate: 0 },
    ],
    frequencies: ["ponctuel", "hebdo", "quotidien"],
    placeholder: "Caractéristiques du site et besoins du contrat (accès, horaires, cahier des charges...)",
  },
};

const inputCls =
  "w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-all duration-300 focus:border-amber-400/60 focus:bg-white/[0.07] focus:shadow-[0_0_20px_rgba(242,193,78,0.1)]";

export const Booking = ({ prefill }) => {
  const [form, setForm] = useState({
    profile: "particulier",
    space_type: "copropriete",
    surface: 120,
    frequency: "ponctuel",
    postal_code: "",
    company: "",
    date: "",
    time: "09:00",
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const profile = PROFILES[form.profile];
  const isAuto = form.space_type === "automobile";

  useEffect(() => {
    if (!prefill) return;
    setForm((f) => {
      const newProfile = prefill.space_type === "bureaux" ? "professionnel" : f.profile;
      return {
        ...f,
        profile: newProfile,
        space_type: prefill.space_type || f.space_type,
        surface: prefill.surface || f.surface,
        frequency: prefill.frequency || f.frequency,
        message: prefill.message || f.message,
      };
    });
  }, [prefill]);

  useEffect(() => {
    const onPreselect = (e) => {
      const d = e.detail || {};
      setForm((f) => {
        const nextProfile = d.profile && PROFILES[d.profile] ? d.profile : f.profile;
        const validSpace = PROFILES[nextProfile].spaces.some((s) => s.id === d.space_type);
        return {
          ...f,
          profile: nextProfile,
          space_type: validSpace ? d.space_type : f.space_type,
          frequency: PROFILES[nextProfile].frequencies.includes(f.frequency) ? f.frequency : "ponctuel",
        };
      });
    };
    window.addEventListener("dimora:preselect", onPreselect);
    return () => window.removeEventListener("dimora:preselect", onPreselect);
  }, []);

  const estimate = useMemo(() => {
    if (isAuto) return { min: 0, max: 0 };
    const space = profile.spaces.find((s) => s.id === form.space_type) || profile.spaces[0];
    const base = space.rate * form.surface;
    const total = base * FREQUENCIES[form.frequency].factor;
    return { min: Math.round(total * 0.85), max: Math.round(total * 1.15) };
  }, [profile, isAuto, form.space_type, form.surface, form.frequency]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const setProfile = (p) =>
    setForm((f) => ({
      ...f,
      profile: p,
      space_type: PROFILES[p].spaces[0].id,
      frequency: PROFILES[p].frequencies.includes(f.frequency) ? f.frequency : "ponctuel",
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date) {
      toast.error("Choisissez une date d'intervention souhaitée.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        surface: isAuto ? 1 : Number(form.surface),
        company: form.profile === "professionnel" ? form.company : "",
        estimate_min: estimate.min,
        estimate_max: estimate.max,
      });
      toast.success("Demande envoyée ! DIMORA vous recontacte sous 24h.");
      setForm({ ...form, name: "", email: "", phone: "", message: "", postal_code: "", company: "", date: "" });
    } catch {
      toast.error("Une erreur est survenue. Réessayez ou appelez-nous au 06 18 90 74 35.");
    } finally {
      setSending(false);
    }
  };

  const deposit = Math.max(10, Math.round(estimate.min * 0.2));
  const spaceLabel = (profile.spaces.find((s) => s.id === form.space_type) || profile.spaces[0]).label.toLowerCase();

  return (
    <section id="devis" data-testid="booking-section" className="py-28 sm:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold mb-4">Devis en ligne</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white max-w-2xl">
            Décrivez votre besoin, <span className="text-amber-300">estimation immédiate</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 rounded-3xl border border-amber-400/25 bg-gradient-to-b from-amber-400/[0.07] to-transparent p-8 sm:p-10 flex flex-col justify-between min-h-[320px]"
            data-testid="booking-estimate-card"
          >
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-slate-400 mb-2">Estimation instantanée</p>
              {isAuto ? (
                <>
                  <p className="font-display text-4xl sm:text-5xl font-extrabold text-white tracking-tight" data-testid="booking-estimate-value">
                    Sur devis
                  </p>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                    L'estimation dépend du véhicule et de son état. Décrivez votre besoin :
                    DIMORA vous répond sous 24h avec une proposition adaptée.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-display text-4xl sm:text-5xl font-extrabold text-white tracking-tight" data-testid="booking-estimate-value">
                    {estimate.min}€ <span className="text-slate-500 text-2xl">—</span> {estimate.max}€
                  </p>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                    {form.frequency === "ponctuel" ? "par intervention" : "par mois"}, pour {form.surface} m² ({spaceLabel}).
                    Proposition définitive après étude de votre demande, sans engagement.
                  </p>
                </>
              )}
            </div>
            <div>
              <div className="mt-8 flex items-center gap-3 text-emerald-300 text-sm font-medium">
                <CalendarCheck className="w-5 h-5" />
                Réponse sous 24h ouvrées
              </div>
              {!isAuto && (
                <button
                  type="button"
                  data-testid="booking-deposit-button"
                  onClick={() => startCheckout("acompte_unite", deposit)}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full border border-amber-400/50 text-amber-200 py-3.5 text-sm font-bold transition-all duration-300 hover:bg-amber-400/10 hover:scale-[1.01] active:scale-[0.98]"
                >
                  <CreditCard className="w-4 h-4" />
                  Bloquer mon créneau — acompte 20% : {deposit}€
                </button>
              )}
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            data-testid="booking-form"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 rounded-3xl border border-white/10 bg-[#111723]/75 card-glow p-8 sm:p-10 space-y-6"
          >
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Vous êtes</label>
              <div className="grid grid-cols-2 gap-3" data-testid="booking-profile-select">
                {Object.values(PROFILES).map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    data-testid={`booking-profile-${p.id}`}
                    onClick={() => setProfile(p.id)}
                    className={`flex items-center justify-center gap-2.5 rounded-2xl border px-4 py-4 text-sm font-bold transition-all duration-300 ${
                      form.profile === p.id
                        ? "border-amber-400 bg-amber-400/10 text-amber-200 shadow-[0_0_25px_rgba(242,193,78,0.15)]"
                        : "border-white/10 text-slate-400 hover:border-white/30 hover:text-white"
                    }`}
                  >
                    <p.icon className="w-5 h-5" strokeWidth={1.5} /> {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Prestation souhaitée</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="booking-space-type-select">
                {profile.spaces.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    data-testid={`booking-space-${s.id}`}
                    onClick={() => setForm({ ...form, space_type: s.id })}
                    className={`rounded-xl border px-3 py-3 text-xs font-semibold transition-all duration-300 ${
                      form.space_type === s.id
                        ? "border-amber-400 bg-amber-400/10 text-amber-200 shadow-[0_0_20px_rgba(242,193,78,0.15)]"
                        : "border-white/10 text-slate-400 hover:border-white/30 hover:text-white"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {!isAuto && (
                <div>
                  <label className="flex justify-between text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">
                    Surface approximative <span className="text-amber-300 font-bold" data-testid="booking-surface-value">{form.surface} m²</span>
                  </label>
                  <input
                    type="range"
                    min="20"
                    max="5000"
                    step="10"
                    value={form.surface}
                    onChange={set("surface")}
                    data-testid="booking-surface-slider"
                    className="w-full mt-3"
                  />
                </div>
              )}
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Fréquence</label>
                <div className="flex gap-2" data-testid="booking-frequency-radio">
                  {profile.frequencies.map((fid) => (
                    <button
                      type="button"
                      key={fid}
                      data-testid={`booking-frequency-${fid}`}
                      onClick={() => setForm({ ...form, frequency: fid })}
                      className={`flex-1 rounded-full border px-3 py-2.5 text-xs font-semibold transition-all duration-300 ${
                        form.frequency === fid
                          ? "border-emerald-400 bg-emerald-400/10 text-emerald-200"
                          : "border-white/10 text-slate-400 hover:border-white/30 hover:text-white"
                      }`}
                    >
                      {FREQUENCIES[fid].label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Date souhaitée</label>
                <input type="date" required value={form.date} onChange={set("date")} data-testid="booking-date-picker" className={inputCls} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Créneau</label>
                <select value={form.time} onChange={set("time")} data-testid="booking-time-select" className={inputCls}>
                  {["07:00", "09:00", "12:00", "14:00", "17:00", "20:00"].map((t) => (
                    <option key={t} value={t} className="bg-[#111723]">
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Code postal</label>
                <input placeholder="05000" value={form.postal_code} onChange={set("postal_code")} data-testid="booking-postal-input" className={inputCls} />
              </div>
            </div>

            {form.profile === "professionnel" && (
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Structure (optionnel)</label>
                <input placeholder="Nom de votre entreprise ou commerce" value={form.company} onChange={set("company")} data-testid="booking-company-input" className={inputCls} />
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <input required placeholder="Nom complet" value={form.name} onChange={set("name")} data-testid="booking-name-input" className={inputCls} />
              <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="booking-email-input" className={inputCls} />
              <input required type="tel" placeholder="Téléphone" value={form.phone} onChange={set("phone")} data-testid="booking-phone-input" className={inputCls} />
            </div>

            <textarea
              placeholder={profile.placeholder}
              rows={3}
              value={form.message}
              onChange={set("message")}
              data-testid="booking-message-input"
              className={`${inputCls} resize-none`}
            />

            <button
              type="submit"
              disabled={sending}
              data-testid="booking-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 py-4 text-sm font-bold text-[#0A0D14] transition-all duration-300 hover:bg-amber-300 hover:scale-[1.01] active:scale-[0.98] hover:shadow-[0_0_40px_rgba(242,193,78,0.45)] disabled:opacity-60"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarCheck className="w-4 h-4" />}
              {sending ? "Envoi en cours..." : "Envoyer ma demande de devis"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
